<script setup>
import BaseModal from '@/components/Alerts/BaseModal.vue'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'
import InputText from '@/components/Forms/InputText.vue'
import { ref, watch } from 'vue'
import MultiListBox from '@/components/Forms/MultiListBox.vue'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { Toaster } from '@/models/utils/Toaster.js';
import { documentsApi } from '@/api/Documents/DocumentsApi.js'
import { UserpilotService } from '@/models/utils/UserpilotUtils.js'
import { useAuthStore } from '@/stores/authStore.js'

const props = defineProps({
	show: { type: Boolean, default: false },//Show modal
	tagData: { type: Array, default: () => [] },
	modalError: { type: Object, default: () => ({ 'error_eh': false, 'error_msg': 'There was an error loading tags. Please try reloading the page.' }) },
	modalData: { type: Object, default: () => ({ "document_name": "", "comment": "", "tag_id": 0 }) },
})

const emit = defineEmits(['evCloseModal'])//Used by ManageDocumentsPage to close modal

const authStore = useAuthStore()
const toaster = new Toaster();

const
	document_name = ref(''),
	comment = ref(''),
	selectedTags = ref([]),
	validationErrors = ref(null),
	showConfirmationModal = ref(false),
	showDiscardChangesModal = ref(false);

watch(
	() => props.show,
	(newValue) =>
	{
		if (newValue)
		{
			//Load data when shown
			handleData(newValue)
		}
	}
)

function handleData()
{
	document_name.value = props.modalData.name
	//comment.value = props.modalData.latest_status_change.comment

	selectedTags.value = []

	for (let i = 0; i < props.modalData.tags.length; i++)
	{
		selectedTags.value.push(props.tagData.find(p_tag => p_tag.id === props.modalData.tags[i].id))
	}
}


/**
 * Checks if there are any changes made to the modal
 * @returns {boolean}
 */
function changesMade()
{
	//let data = props.modalData
	return (comment.value != '')
}

/**
 * Show or Hide the Discard Changes modal
 * @param {boolean} p_show_eh True to show, false to hide
 */
function toggleDiscardChangesModal(p_show_eh)
{
	showDiscardChangesModal.value = p_show_eh
}

/**
 * Show or Hide the confirmation
 * @param {boolean} p_show_eh True to show, false to hide
 */
function toggleConfirmationModal(p_show_eh)
{
	showConfirmationModal.value = p_show_eh
}

function deleteDocument()
{
	documentsApi.deleteDocument(
		{
			'document': {
				'id': props.modalData.id,
				'organization_id': props.modalData.organization_id
			},
			'status': {
				'comment': comment.value,
			}
		},
		(p_response) => {
			console.log('deleteDocument.success:', p_response)

			UserpilotService.track('documents_delete', {
				"user_id": authStore.getAuth.user().id,
				"organization_id": authStore.activeOrganizationId
			});

			closeModal(true)
		},
		(p_err) => {
			console.log('deleteDocument.error:', p_err)
			validationErrors.value = p_err.showErrorBanner()
			//modalError.value = { 'error_eh': true, 'error_msg': 'Error loading Tags. Please try reloading the page.' }
		}

	)
}

/**
 * Check if there's any changes, if not, proceed with closing the modal
 * @param p_reload_eh True to reload the upload grid
 */
function dismissModal(p_reload_eh)
{
	if (!p_reload_eh && !props.modalError.error_eh && changesMade())
	{
		toggleDiscardChangesModal(true)
	}
	else
	{
		closeModal(p_reload_eh)
	}
}

/**
 * Do cleanup, show success toast and tell the UploadDocumentPage to close the modal
 * @param p_reload_eh
 */
function closeModal(p_reload_eh)
{
	cleanupModal()
	if (p_reload_eh) toaster.success("Document deleted successfully.")
	emit('evCloseModal', 'delete', p_reload_eh)
}

/**
 * Tidy up
 */
function cleanupModal()
{
	toggleDiscardChangesModal(false)
	toggleConfirmationModal(false)
	validationErrors.value = null
}
</script>

<template>
	<div>
		<BaseModal :show="show" border-classes="border-t-6 border-tertiary">
			<div class="w-136 h-192">
				<div class="flex justify-between border-b border-neutral w-full pb-6">
					<p class="flex-grow text-xl font-bold text-heading">Delete Document</p>
					<img src="@/assets/images/icon/dismiss.svg" alt="Dismiss" title="Dismiss" class="cursor-pointer size-4" @click.prevent="dismissModal(false)">
				</div>

				<div v-if="props.modalError.error_eh">
					<div class="w-130 mt-6 text-error font-bold">{{ props.modalError.error_msg }}</div>
				</div>

				<form>
					<div class="flex flex-col mt-5">
						<InputText label="Document name" :disabled="true" :required="true" type="text" placeholder="" input-height="h-10" autocomplete="off"
									  v-model="document_name"  icon="edit" />

						<MultiListBox id="tags" v-model="selectedTags" title="Tags" placeholder="Select a tag" :disabled="true"
										  label-class="font-medium text-label !mb-2" input-height="h-10" icon="edit" maxSearchResultsHeight="max-h-60"
										  :items="tagData"
										  :show-options-on-top="false" />

						<InputText label="Notes*" class="mt-3" :required="true" type="textarea" placeholder="" input-height="h-10" autocomplete="off"
									  v-model="comment" :errors="validationErrors" error-field="status.comment" />

					</div>
					<!-- Save/Cancel -->
					<div class="flex w-full flex-col-reverse sm:flex-row mt-4">
						<div class="sm:mr-auto mt-1 sm:mt-0"></div><!-- remove if showing Deactivate -->
						<SubmitButton label="Cancel" :type="ButtonType.Outline" :size="ButtonSize.Small" @click.prevent="dismissModal(false)"
										  class="mt-3 sm:mt-0" :class="{'sm:mr-4': true}" />
						<SubmitButton label="Delete" :type="ButtonType.Warning" :size="ButtonSize.Small" @click.prevent="toggleConfirmationModal(true)"  />

					</div>
				</form>
			</div>
		</BaseModal>

		<Teleport to="html">
			<MultiButtonModal
				:show="showDiscardChangesModal"
				icon="info"
				title="Discard Changes"
				message="Are you sure?"
				subMessage="Leaving now will discard any changes made."
				:buttons="[
									new ModalButtonData(
										'Cancel', () => { toggleDiscardChangesModal(false); }, ButtonType.Outline, ButtonSize.Small
									),
									new ModalButtonData(
										'Yes', () => { closeModal(false); }, ButtonType.Default, ButtonSize.Small
									)
								]"
			/>
		</Teleport>

		<Teleport to="html">
			<MultiButtonModal
				:show="showConfirmationModal"
				icon="error"
				title="Delete Document"
				message="Are you sure?"
				subMessage="Deleting this document cannot be undone."
				:buttons="[
									new ModalButtonData(
										'Cancel', () => { toggleConfirmationModal(false); }, ButtonType.Outline, ButtonSize.Small
									),
									new ModalButtonData(
										'Yes', () => { toggleConfirmationModal(false); deleteDocument(); }, ButtonType.Default, ButtonSize.Small
									)
								]"
			/>
		</Teleport>

	</div>
</template>

<style scoped>
</style>
